import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import APIKit from '../../config/APIKit';

// Create a separate API instance for fetching stores without store-key header
const StoresAPIKit = APIKit.create({
  baseURL: process.env.REACT_APP_BASE_URL || "https://eateazyapi.yorotechnologies.com/api",
  timeout: 1000000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Remove the store-key interceptor for stores endpoint
StoresAPIKit.interceptors.request.use(
  async (config) => {
    // Only add auth token if available, no store-key for stores listing
    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers = { 
        ...config.headers, 
        "x-api-key": `${token}`,
        "Authorization": `Bearer ${token}`
      };
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

// Mock data for demonstration
const mockStores = [
  {
    id: 1,
    key: 'shahama-restaurant',
    name: 'Shahama Restaurant',
    description: 'Authentic Middle Eastern cuisine with fresh ingredients and traditional recipes passed down through generations.',
    area: 'Downtown Dubai',
    phone: '+971 4 123 4567',
    from_time: '10:00',
    to_time: '23:00',
    status: true,
    DocImage: {
      imageUrl: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=500&q=80'
    }
  },
  {
    id: 2,
    key: 'pizza-corner',
    name: 'Pizza Corner',
    description: 'Wood-fired pizzas with premium toppings and fresh ingredients. Perfect for family dining and casual meals.',
    area: 'Jumeirah',
    phone: '+971 4 234 5678',
    from_time: '11:00',
    to_time: '01:00',
    status: true,
    DocImage: {
      imageUrl: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?auto=format&fit=crop&w=500&q=80'
    }
  },
  {
    id: 3,
    key: 'sushi-master',
    name: 'Sushi Master',
    description: 'Fresh sushi and Japanese cuisine prepared by expert chefs. Experience authentic flavors from the Land of the Rising Sun.',
    area: 'Marina Walk',
    phone: '+971 4 345 6789',
    from_time: '12:00',
    to_time: '22:30',
    status: true,
    DocImage: {
      imageUrl: 'https://images.unsplash.com/photo-1579584425555-c3ce17fd4351?auto=format&fit=crop&w=500&q=80'
    }
  },
  {
    id: 4,
    key: 'burger-hub',
    name: 'Burger Hub',
    description: 'Gourmet burgers with premium beef, fresh vegetables, and artisanal buns. A paradise for burger lovers.',
    area: 'City Centre',
    phone: '+971 4 456 7890',
    from_time: '09:00',
    to_time: '24:00',
    status: true,
    DocImage: {
      imageUrl: 'https://images.unsplash.com/photo-1571091718767-18b5b1457add?auto=format&fit=crop&w=500&q=80'
    }
  },
  {
    id: 5,
    key: 'cafe-delight',
    name: 'Café Delight',
    description: 'Cozy café serving specialty coffee, fresh pastries, and light meals. Perfect for breakfast and afternoon tea.',
    area: 'Business Bay',
    phone: '+971 4 567 8901',
    from_time: '07:00',
    to_time: '20:00',
    status: true,
    DocImage: {
      imageUrl: 'https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?auto=format&fit=crop&w=500&q=80'
    }
  },
  {
    id: 6,
    key: 'spice-garden',
    name: 'Spice Garden',
    description: 'Authentic Indian cuisine with aromatic spices and traditional cooking methods. A journey through the flavors of India.',
    area: 'Bur Dubai',
    phone: '+971 4 678 9012',
    from_time: '11:30',
    to_time: '23:30',
    status: true,
    DocImage: {
      imageUrl: 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?auto=format&fit=crop&w=500&q=80'
    }
  },
  {
    id: 7,
    key: 'noodle-house',
    name: 'Noodle House',
    description: 'Fresh hand-pulled noodles and authentic Chinese dishes. Experience the art of traditional Chinese cooking.',
    area: 'Deira',
    phone: '+971 4 789 0123',
    from_time: '10:30',
    to_time: '22:00',
    status: false, // This one is closed
    DocImage: {
      imageUrl: 'https://images.unsplash.com/photo-1569718212165-3a8278d5f624?auto=format&fit=crop&w=500&q=80'
    }
  },
  {
    id: 8,
    key: 'mediterranean-breeze',
    name: 'Mediterranean Breeze',
    description: 'Fresh Mediterranean cuisine with olive oil, herbs, and seasonal ingredients. Healthy and delicious dining experience.',
    area: 'Palm Jumeirah',
    phone: '+971 4 890 1234',
    from_time: '12:00',
    to_time: '23:00',
    status: true,
    DocImage: {
      imageUrl: 'https://images.unsplash.com/photo-1544148103-0773bf10d330?auto=format&fit=crop&w=500&q=80'
    }
  },
  {
    id: 9,
    key: 'steak-house',
    name: 'Prime Steak House',
    description: 'Premium steaks grilled to perfection with fine wines and elegant atmosphere. Perfect for special occasions.',
    area: 'DIFC',
    phone: '+971 4 901 2345',
    from_time: '18:00',
    to_time: '01:00',
    status: true,
    DocImage: {
      imageUrl: 'https://images.unsplash.com/photo-1546833999-b9f581a1996d?auto=format&fit=crop&w=500&q=80'
    }
  },
  {
    id: 10,
    key: 'thai-spice',
    name: 'Thai Spice',
    description: 'Authentic Thai cuisine with perfect balance of sweet, sour, salty, and spicy flavors. A taste of Thailand in Dubai.',
    area: 'JBR',
    phone: '+971 4 012 3456',
    from_time: '11:00',
    to_time: '23:00',
    status: true,
    DocImage: {
      imageUrl: 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?auto=format&fit=crop&w=500&q=80'
    }
  }
];

export const fetchStores = createAsyncThunk(
  'stores/fetchStores',
  async (_, { rejectWithValue }) => {
    try {
      const response = await StoresAPIKit.get('store/list/all');
      
      if (response && Array.isArray(response.data?.data)) {
        return response.data?.data||[];
      } else {
        return rejectWithValue('Failed to fetch stores');
      }
    } catch (error) {
      if (error && typeof error === 'object' && error.response) {
        return rejectWithValue(error.response?.data || 'Failed to fetch stores');
      }
      return rejectWithValue('Failed to fetch stores');
    }
  }
);

const storesSlice = createSlice({
  name: 'stores',
  initialState: {
    stores: [],
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
  },
  reducers: {
    clearStores: (state) => {
      state.stores = [];
      state.status = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStores.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchStores.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.stores = action.payload;
        state.error = null;
      })
      .addCase(fetchStores.rejected, (state, action) => {
        state.status = 'failed';
        state.error = typeof action.payload === 'string' ? action.payload : 'Failed to fetch stores';
      });
  },
});

export const { clearStores } = storesSlice.actions;
export default storesSlice.reducer;
